import type {
  BatchConfig,
  CheckpointConfig,
  MiddlewareConfig,
  SchemaConfig,
  StageConfig,
} from "@/types/api";

// The parsed pipeline document's view shape and the schema-alias rules —
// shared by the AI helper's context, the pipeline code generation and the
// chat sidebar. (Extracted from the old /studio's usePipelineState ahead
// of its deletion, TTTL-179.)

export interface PipelineView {
  name: string;
  enabled: boolean;
  source: StageConfig | null;
  middlewares: MiddlewareConfig[];
  sinks: StageConfig[];
  schemas: SchemaConfig[];
  checkpoint: CheckpointConfig | null;
  batch: BatchConfig | null;
  parseError: string | null;
  /** The code as written, kept only when it does not parse: the helper's
   *  structured pipeline item cannot be built from it, so the text goes
   *  instead. */
  code?: string;
}

const ALIAS_ADJECTIVES = [
  "fabulous",
  "curious",
  "nimble",
  "gentle",
  "brave",
  "clever",
  "swift",
  "calm",
  "bold",
  "keen",
  "merry",
  "vivid",
  "proud",
  "witty",
  "bright",
];

const ALIAS_ANIMALS = [
  "behemoth",
  "manatee",
  "pangolin",
  "capybara",
  "axolotl",
  "quokka",
  "narwhal",
  "ocelot",
  "fennec",
  "toucan",
  "wombat",
  "ibis",
  "jerboa",
  "kiwi",
  "lemur",
];

function generateAlias(existing: Set<string>): string {
  for (const adj of ALIAS_ADJECTIVES) {
    for (const animal of ALIAS_ANIMALS) {
      const candidate = `${adj}-${animal}`;
      if (!existing.has(candidate)) return candidate;
    }
  }
  let i = 1;
  while (existing.has(`schema-${i}`)) i++;
  return `schema-${i}`;
}

// The schemas-block alias a stage's schema_id points at — the schema's
// real name (slugged to id-safe characters), deduped with a numeric
// suffix. The adjective-animal generator only covers refs with no usable
// name left after slugging.
export function schemaAlias(resource: string, existing: Set<string>): string {
  const base = resource
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!base) return generateAlias(existing);
  if (!existing.has(base)) return base;
  let i = 2;
  while (existing.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}
